import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { authService, AuthResponse, AuthUser, OAuthProvider } from '@/services/authService';
import { analytics } from '@/services/analytics';
import { clearLocalProgress, initProgressSync, resetProgressForSignOut } from '@/utils/audioProgressUtils';
import { isNativeApp } from '@/utils/platform';
import { signInWithApple, signInWithGoogle } from '@/native/socialAuth';

// Per-attempt sign-in options. forceAccountPicker shows the provider's account
// chooser instead of silently reusing the last account (native Google only;
// used on the sign-up path). No effect on the web redirect flow.
export interface LoginOptions {
  forceAccountPicker?: boolean;
}

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (provider: OAuthProvider, options?: LoginOptions) => Promise<void>;
  logout: () => Promise<void>;
  deleteAccount: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Native-shell sign-in: run the platform sheet for the provider, then verify
// the returned token with the backend and return the Aleph session. Throws
// SocialAuthCancelledError if the user dismisses the sheet (callers ignore it).
const nativeLogin = async (provider: OAuthProvider, options?: LoginOptions): Promise<AuthResponse> => {
  if (provider === 'apple') {
    const credential = await signInWithApple();
    return authService.nativeAppleSignIn({
      identity_token: credential.identityToken,
      given_name: credential.givenName,
      family_name: credential.familyName,
    });
  }
  if (provider === 'google') {
    const credential = await signInWithGoogle({ forcePrompt: options?.forceAccountPicker });
    return authService.nativeGoogleSignIn({ id_token: credential.idToken });
  }
  throw new Error(`Native sign-in is not supported for provider: ${provider}`);
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Seeded from the device's stored session so isAuthenticated is already right
  // on the first paint. initializeAuth() below revalidates against the server
  // and clears this if the session has actually gone; until it settles the
  // guess is unconfirmed, which is what isLoading marks. Starting at null
  // instead meant every reload rendered a frame of signed-out UI to a signed-in
  // user - the "Sign in to read" flash on /read/:slug, the missing profile
  // icon, the absent player. Anything with a side effect (a request, an event,
  // a redirect) must therefore wait for isLoading to clear rather than trusting
  // isAuthenticated alone; rendering can use it immediately.
  // hasStoredCredentials() ignores token expiry on purpose: an expired access
  // token with a refresh token behind it is still a live session.
  const [user, setUser] = useState<AuthUser | null>(() =>
    (authService.hasStoredCredentials() ? authService.getStoredUser() : null),
  );
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user;

  useEffect(() => {
    initializeAuth();
  }, []);

  // When a user is authenticated, reconcile local progress cookies with the
  // server (one-time cookie->server migration, then hydrate cross-device state).
  // Held until initializeAuth() has confirmed the session: the seeded user is
  // only a guess, and this writes the device's progress to whatever account it
  // names.
  useEffect(() => {
    if (isLoading || !isAuthenticated || !user) return;
    void initProgressSync(user.id);
  }, [isLoading, isAuthenticated, user?.id]);

  // What to show after an auth step failed, without re-deciding *why* it failed.
  // authService clears stored credentials only when the server explicitly
  // rejected the session, so the credentials surviving IS the signal that this
  // was an outage rather than a sign-out. Reading that here keeps the
  // classification in one place instead of duplicating it per call site.
  const userAfterFailure = (): AuthUser | null =>
    (authService.hasStoredCredentials() ? authService.getStoredUser() : null);

  const initializeAuth = async () => {
    try {
      setIsLoading(true);

      const storedUser = authService.getStoredUser();
      const token = authService.getAccessToken();
      const refreshToken = authService.getRefreshToken();

      // If access token is expired but we have a refresh token, try to refresh
      if (storedUser && token && authService.isTokenExpired() && refreshToken) {
        try {
          await authService.refreshAccessToken();
          const currentUser = await authService.getCurrentUser();
          setUser(currentUser);
          return;
        } catch (error) {
          // An expired access token that could not be refreshed offline is
          // still a live session - the refresh token outlives it by days, and
          // makeAuthenticatedRequest() retries the refresh on demand.
          console.warn('Failed to refresh expired token:', error);
          setUser(userAfterFailure());
          return;
        }
      }

      if (storedUser && token && !authService.isTokenExpired()) {
        try {
          const currentUser = await authService.getCurrentUser();
          setUser(currentUser);

          // Schedule token refresh if we have valid tokens
          const tokenExpiry = authService.getTokenExpiry();
          if (tokenExpiry) {
            const expiresIn = Math.max(0, Math.floor((tokenExpiry - Date.now()) / 1000));
            authService.scheduleTokenRefresh(expiresIn);
          }
        } catch (error) {
          console.warn('Failed to validate stored tokens:', error);
          setUser(userAfterFailure());
        }
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Auth initialization error:', error);
      setUser(userAfterFailure());
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (provider: OAuthProvider, options?: LoginOptions) => {
    try {
      setIsLoading(true);

      // Native app: drive the platform sign-in sheet and exchange the resulting
      // identity/ID token for an Aleph session inline (no page redirect). The
      // token is stored by authService; we set the user here so the app reacts
      // (app.tsx closes the auth modal and routes on isAuthenticated).
      if (isNativeApp()) {
        const authResponse = await nativeLogin(provider, options);
        authService.setLastLoginMethod(provider);
        setUser(authResponse.user);
        analytics.identify(authResponse.user.id);
        analytics.register({ user_id: authResponse.user.id });
        setIsLoading(false);
        return;
      }

      const authUrl = await authService.initiateOAuthLogin(provider);

      window.location.href = authUrl;
    } catch (error) {
      console.error('Login error:', error);
      setIsLoading(false);
      throw error;
    }
  };

  const logout = async () => {
    try {
      setIsLoading(true);
      // Before the token goes: push whatever progress is still queued, then
      // empty the device store. Progress entries are keyed by book alone, so
      // anything left behind is read — and re-uploaded — as the next account's.
      await resetProgressForSignOut();
      await authService.logout();
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Rethrows on failure so the caller can surface the error; no setIsLoading here,
  // since flipping the app into the auth-loading state would unmount the caller's UI.
  const deleteAccount = async () => {
    await authService.deleteAccount();
    // No flush here — the account it would push to no longer exists.
    clearLocalProgress();
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);
      if (currentUser) {
        analytics.identify(currentUser.id);
        analytics.register({ user_id: currentUser.id });
      }

    } catch (error) {
      console.warn('Refresh user error:', error);
      setUser(userAfterFailure());
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    deleteAccount,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
